import React from "react";
import type { U2000TableColumn } from "./U2000BasicTableList";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import { U2000ApiValueToDisplayYmd } from "../../../tools/U2000DateTimeHelpers";

type U2000BasicTableListRowProps = {
  gridStateHandlers: U2000GridStateHandlers<any>;
  row: Record<string, any>;
  rowClickFn: (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: Record<string, any>) => void;
  rowUpdated?: (row: any) => void;
  even?: boolean;
  allowRowSelection: boolean;
  enableHorizontalScroll?: boolean;
};

const ALIGN_CLASS: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce((acc: unknown, part) => (acc && typeof acc === "object" && part in acc ? (acc as Record<string, unknown>)[part] : undefined), obj);
}

const formatValue = (column: U2000TableColumn, value: unknown, row: Record<string, any>, allowRowSelection: boolean, rowUpdated?: (row: any) => void): React.ReactNode => {
  if (typeof column.display === "function") {
    return column.display(column, value, row, allowRowSelection, rowUpdated);
  }

  if (value == null) return null;

  switch (column.type) {
    case "date": {
      const ymd = U2000ApiValueToDisplayYmd(value);
      return ymd ?? String(value);
    }
    case "currency":
      return `$${Number(value).toFixed(2)}`;
    case "uppercase":
      return String(value).toUpperCase();
    default:
      return value as React.ReactNode;
  }
};

const U2000BasicTableListRow: React.FC<U2000BasicTableListRowProps> = ({
  gridStateHandlers,
  row,
  rowClickFn,
  even,
  rowUpdated,
  allowRowSelection,
  enableHorizontalScroll = false,
}) => {
  // Crée une map pour retrouver une colonne complète par son nom
  const columnMap = new Map<string, U2000TableColumn>(gridStateHandlers.getColumns().map((col) => [col.name, col]));

  return (
    <tr
      className={`group focus-within:outline-hidden focus-within:ring-2 focus-within:ring-primary-400 ${
        even ? "even:bg-gray-50" : ""
      } hover:bg-blue-50 cursor-pointer bg-white transition-colors duration-150 border-b border-gray-200 last:border-b-0 text-sm`}
      tabIndex={0}
      role="row"
    >
      {(() => {
        const column = columnMap.get("id");
        if (!column) return null;

        const value = getNestedValue(row, column.name);
        const content = allowRowSelection ? (
          formatValue(column, value, row, allowRowSelection, rowUpdated)
        ) : (
          <span className="opacity-50 cursor-not-allowed select-none">{formatValue(column, value, row, allowRowSelection, rowUpdated)}</span>
        );

        return (
          <td
            key={column.name}
            className={`${ALIGN_CLASS[column.align ?? "left"]} px-2 py-2 border-r last:border-r-0 border-gray-200 ${column.widthClass ?? "min-w-[100px]"} whitespace-nowrap`}
          >
            {content ?? <span className="text-gray-400">—</span>}
          </td>
        );
      })()}

      {gridStateHandlers.getSelectedFields().map((fieldName: string) => {
        const column = columnMap.get(fieldName);
        if (!column) return null; // Ignore si champ non trouvé

        const value = getNestedValue(row, column.name);
        let content = formatValue(column, value, row, allowRowSelection, rowUpdated);
        // Désactive la checkbox si allowRowSelection est false
        const isCheckbox = React.isValidElement(content) && content.type === "input" && (content.props as any)?.type === "checkbox";
        if (!allowRowSelection && isCheckbox && React.isValidElement(content)) {
          const props = (content as React.ReactElement).props as any;
          content = React.cloneElement(content as React.ReactElement<React.InputHTMLAttributes<HTMLInputElement>>, {
            disabled: true,
            className: (props.className || "") + " opacity-50 cursor-not-allowed",
          });
        }
        const cellTruncateClass =
          column.noTruncate || enableHorizontalScroll ? "whitespace-nowrap" : "truncate";
        const cellTitle =
          value != null && (typeof value !== "object" || value instanceof Date) ? String(value) : undefined;
        const cellContent =
          !column.noTruncate && !enableHorizontalScroll && content != null ? (
            <span className="block min-w-0 truncate">{content}</span>
          ) : (
            content ?? <span className="text-gray-400">—</span>
          );
        return (
          <td
            key={column.name}
            title={cellTitle}
            className={`${ALIGN_CLASS[column.align ?? "left"]} px-4 py-2 border-r last:border-r-0 border-gray-100 ${column.widthClass ?? "min-w-[100px]"} ${cellTruncateClass}${!allowRowSelection && isCheckbox ? " cursor-not-allowed" : ""}`}
            {...(!allowRowSelection && isCheckbox ? { role: "cell" } : { onClick: (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => rowClickFn && rowClickFn(event, row), role: "cell" })}
          >
            {cellContent}
          </td>
        );
      })}
    </tr>
  );
};

export default U2000BasicTableListRow;
